import { useState, useCallback, useEffect, useRef } from 'react';
import { GameState, Company, BuildingPosition } from '../../../types/career';

const BUILDING_WIDTH = 150;
const BUILDING_GAP = 80;
const CHARACTER_SPEED = 200; // pixels per second
const VIEWPORT_WIDTH = 1000;

interface UseGameStateProps {
  companies: Company[];
  viewportWidth?: number;
  speedMultiplier?: number;
}

interface UseGameStateResult {
  gameState: GameState;
  buildingPositions: BuildingPosition[];
  worldWidth: number;
  moveCharacter: (direction: 'left' | 'right') => void;
  stopCharacter: () => void;
  walkToPosition: (x: number) => void;
  selectBuilding: (id: string | null) => void;
  toggleBuildingExpansion: (id: string) => void;
  closeBuildingExpansion: () => void;
  getNearbyBuilding: (side?: 'top' | 'bottom') => string | null;
}

export const useGameState = ({
  companies,
  viewportWidth = VIEWPORT_WIDTH,
  speedMultiplier = 1
}: UseGameStateProps): UseGameStateResult => {
  const [gameState, setGameState] = useState<GameState>({
    characterX: 100,
    viewportOffset: 0,
    selectedBuilding: null,
    expandedBuilding: null,
    isWalking: false,
    facingDirection: 'right',
    targetX: null,
  });

  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const movementDirectionRef = useRef<'left' | 'right' | null>(null);
  const speedMultiplierRef = useRef(speedMultiplier);

  // Keep speed multiplier ref updated
  useEffect(() => {
    speedMultiplierRef.current = speedMultiplier;
  }, [speedMultiplier]);

  // Calculate building positions
  const buildingPositions: BuildingPosition[] = companies.map((company, index) => ({
    id: company.id,
    x: index * (BUILDING_WIDTH + BUILDING_GAP) + BUILDING_GAP,
    width: BUILDING_WIDTH,
    side: company.side,
  }));

  // Calculate character movement bounds (stay within building area)
  const firstBuildingCenter = buildingPositions.length > 0
    ? buildingPositions[0].x + BUILDING_WIDTH / 2
    : BUILDING_GAP;
  const lastBuildingCenter = buildingPositions.length > 0
    ? buildingPositions[buildingPositions.length - 1].x + BUILDING_WIDTH / 2
    : BUILDING_GAP;

  // World width must allow viewport to center on both first and last buildings
  // When centered on last building: offset = lastBuildingCenter - viewportWidth/2
  // For this to work: worldWidth - viewportWidth >= offset
  // So: worldWidth >= lastBuildingCenter + viewportWidth/2
  const worldWidth = lastBuildingCenter + viewportWidth / 2;

  // Animation loop
  const animate = useCallback((timestamp: number) => {
    if (lastTimeRef.current === 0) {
      lastTimeRef.current = timestamp;
    }

    const deltaTime = (timestamp - lastTimeRef.current) / 1000;
    lastTimeRef.current = timestamp;

    setGameState(prev => {
      let newX = prev.characterX;
      let newIsWalking = prev.isWalking;
      let newFacingDirection = prev.facingDirection;
      let newTargetX = prev.targetX;

      const currentSpeed = CHARACTER_SPEED * speedMultiplierRef.current;

      // Handle keyboard movement
      if (movementDirectionRef.current) {
        const delta = currentSpeed * deltaTime;
        if (movementDirectionRef.current === 'left') {
          newX = Math.max(firstBuildingCenter, prev.characterX - delta);
          newFacingDirection = 'left';
        } else {
          newX = Math.min(lastBuildingCenter, prev.characterX + delta);
          newFacingDirection = 'right';
        }
        newIsWalking = true;
        newTargetX = null;
      }
      // Handle click-to-walk
      else if (prev.targetX !== null) {
        const delta = currentSpeed * deltaTime;
        const distance = prev.targetX - prev.characterX;

        if (Math.abs(distance) < delta) {
          newX = prev.targetX;
          newTargetX = null;
          newIsWalking = false;
        } else {
          newX = prev.characterX + Math.sign(distance) * delta;
          newFacingDirection = distance > 0 ? 'right' : 'left';
          newIsWalking = true;
        }
      } else {
        newIsWalking = false;
      }

      // Calculate viewport offset to follow character
      const viewportCenter = viewportWidth / 2;
      let newViewportOffset = newX - viewportCenter;

      // Clamp viewport to world bounds
      newViewportOffset = Math.max(0, Math.min(worldWidth - viewportWidth, newViewportOffset));

      return {
        ...prev,
        characterX: newX,
        viewportOffset: newViewportOffset,
        isWalking: newIsWalking,
        facingDirection: newFacingDirection,
        targetX: newTargetX,
      };
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [worldWidth, viewportWidth, firstBuildingCenter, lastBuildingCenter]);

  // Start/stop animation loop
  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animate]);

  const moveCharacter = useCallback((direction: 'left' | 'right') => {
    movementDirectionRef.current = direction;
    setGameState(prev => ({ ...prev, targetX: null, expandedBuilding: null }));
  }, []);

  const stopCharacter = useCallback(() => {
    movementDirectionRef.current = null;
  }, []);

  const walkToPosition = useCallback((x: number) => {
    movementDirectionRef.current = null;
    setGameState(prev => ({
      ...prev,
      targetX: Math.max(firstBuildingCenter, Math.min(lastBuildingCenter, x)),
    }));
  }, [firstBuildingCenter, lastBuildingCenter]);

  const selectBuilding = useCallback((id: string | null) => {
    setGameState(prev => ({ ...prev, selectedBuilding: id }));
  }, []);

  const toggleBuildingExpansion = useCallback((id: string) => {
    setGameState(prev => ({
      ...prev,
      expandedBuilding: prev.expandedBuilding === id ? null : id,
    }));
  }, []);

  const closeBuildingExpansion = useCallback(() => {
    setGameState(prev => ({ ...prev, expandedBuilding: null }));
  }, []);

  const getNearbyBuilding = useCallback((side?: 'top' | 'bottom'): string | null => {
    const PROXIMITY_THRESHOLD = 80;

    for (const pos of buildingPositions) {
      // If side is specified, only check buildings on that side
      if (side && pos.side !== side) {
        continue;
      }
      const buildingCenter = pos.x + pos.width / 2;
      if (Math.abs(gameState.characterX - buildingCenter) < PROXIMITY_THRESHOLD) {
        return pos.id;
      }
    }
    return null;
  }, [buildingPositions, gameState.characterX]);

  return {
    gameState,
    buildingPositions,
    worldWidth,
    moveCharacter,
    stopCharacter,
    walkToPosition,
    selectBuilding,
    toggleBuildingExpansion,
    closeBuildingExpansion,
    getNearbyBuilding,
  };
};
